export const CONTAINER_OPTIONS = [
  { value: 'freezer_contents', label: 'Freezer' },
  { value: 'freezer_door_contents', label: 'Freezer Door' },
  { value: 'fridge_level_contents', label: 'Fridge Shelf' },
  { value: 'fridge_door_contents', label: 'Fridge Door' },
  { value: 'crisper_left_contents', label: 'Crisper Left' },
  { value: 'crisper_right_contents', label: 'Crisper Right' },
  { value: 'pantry_contents', label: 'Pantry' },
  { value: 'spice_rack_contents', label: 'Spice Rack' },
  { value: 'other_contents', label: 'Other' },
];

export const CONTAINER_KEYS = CONTAINER_OPTIONS.map(({ value }) => value);

const createEmptyState = () =>
  CONTAINER_KEYS.reduce((state, key) => {
    state[key] = [];
    return state;
  }, {});

const initialState = createEmptyState();

const createEmptyFoodItem = () => ({
  id: '',
  name: '',
  quantity: '',
  expirationDate: '',
});

const normalizeItem = (item = {}, fallbackId = '') => ({
  ...createEmptyFoodItem(),
  ...item,
  id: item.id ?? fallbackId,
});

const normalizeContainerItems = (items = []) =>
  Array.isArray(items)
    ? items.map((item, index) => normalizeItem(item, `${item?.name ?? 'item'}-${index}`))
    : [];

const getNormalizedContainerState = (containers = {}) =>
  CONTAINER_KEYS.reduce((state, key) => {
    state[key] = normalizeContainerItems(containers[key]);
    return state;
  }, createEmptyState());

const getLegacyContainerState = (containers = {}) => ({
  freezer_contents: normalizeContainerItems(containers.freezer),
  fridge_level_contents: normalizeContainerItems(containers.fridge),
  pantry_contents: normalizeContainerItems(containers.pantry),
  other_contents: normalizeContainerItems(containers.other),
});

const getContainerKeyFromAction = (actionType) => {
  switch (actionType) {
    case 'ADD_FREEZER_ITEM':
    case 'REMOVE_FREEZER_ITEM':
      return 'freezer_contents';
    case 'ADD_FRIDGE_ITEM':
    case 'REMOVE_FRIDGE_ITEM':
      return 'fridge_level_contents';
    default:
      return null;
  }
};

function reducer(state = initialState, action) {
  switch (action.type) {
    case 'HYDRATE_USER_DATABASE': {
      const databaseContainers = action.payload?.containers ?? {};
      const normalizedState = getNormalizedContainerState(databaseContainers);
      const legacyState = getLegacyContainerState(databaseContainers);

      return {
        ...createEmptyState(),
        ...legacyState,
        ...normalizedState,
      };
    }
    case 'ADD_ITEM': {
      const { containerKey, item } = action.payload ?? {};

      if (!CONTAINER_KEYS.includes(containerKey) || !item) {
        return state;
      }

      return {
        ...state,
        [containerKey]: [
          ...state[containerKey],
          normalizeItem(item, `${item.name}-${state[containerKey].length}`),
        ],
      };
    }
    case 'REMOVE_ITEM': {
      const { containerKey, itemId } = action.payload ?? {};

      if (!CONTAINER_KEYS.includes(containerKey) || !itemId) {
        return state;
      }

      return {
        ...state,
        [containerKey]: state[containerKey].filter((item) => item.id !== itemId),
      };
    }
    case 'UPDATE_ITEM': {
      const { containerKey, itemId, updates } = action.payload ?? {};

      if (!CONTAINER_KEYS.includes(containerKey) || !itemId || !updates) {
        return state;
      }

      return {
        ...state,
        [containerKey]: state[containerKey].map((item) =>
          item.id === itemId ? normalizeItem({ ...item, ...updates }, itemId) : item
        ),
      };
    }
    case 'MOVE_ITEM': {
      const { sourceContainerKey, destinationContainerKey, item } = action.payload ?? {};

      if (
        !CONTAINER_KEYS.includes(sourceContainerKey) ||
        !CONTAINER_KEYS.includes(destinationContainerKey) ||
        !item?.id
      ) {
        return state;
      }

      if (sourceContainerKey === destinationContainerKey) {
        return state;
      }

      const normalizedItem = normalizeItem(item, item.id);

      return {
        ...state,
        [sourceContainerKey]: state[sourceContainerKey].filter(
          (existingItem) => existingItem.id !== item.id
        ),
        [destinationContainerKey]: [
          ...state[destinationContainerKey].filter(
            (existingItem) => existingItem.id !== item.id
          ),
          normalizedItem,
        ],
      };
    }
    case 'RESET_USER_DATABASE':
      return createEmptyState();
    case 'ADD_FREEZER_ITEM':
    case 'ADD_FRIDGE_ITEM': {
      const containerKey = getContainerKeyFromAction(action.type);

      if (!containerKey) {
        return state;
      }

      return reducer(state, {
        type: 'ADD_ITEM',
        payload: { containerKey, item: action.payload },
      });
    }
    case 'REMOVE_FREEZER_ITEM':
    case 'REMOVE_FRIDGE_ITEM': {
      const containerKey = getContainerKeyFromAction(action.type);

      if (!containerKey) {
        return state;
      }

      return reducer(state, {
        type: 'REMOVE_ITEM',
        payload: { containerKey, itemId: action.payload },
      });
    }
    default:
      return state;
  }
}

export default reducer;
